import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  ChevronLeft, 
  Star, 
  Quote,
  MessageSquare
} from "lucide-react";
import axios from "axios";
import { PublicNav } from "@/components/public/PublicNav";
import { PublicFooter } from "@/components/public/PublicFooter";
import { usePublicData } from "@/hooks/usePublicData";
import { useSeoHeadTags } from "@/hooks/useSeoHeadTags";
import { SITE_ORIGIN } from "@/lib/publicSite";

const API = `${window.location.origin}/api`;

const StarDisplay = ({ rating }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((star) => (
      <Star 
        key={star}
        className={`h-4 w-4 ${star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
      />
    ))}
  </div>
);

export default function ReviewsPage() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const { bizInfo, services } = usePublicData();

  useSeoHeadTags({
    title: "Customer Reviews | Handyman Abergavenny | 5-Star Rated",
    description: "Read reviews from satisfied customers of Sustainable Handyman Services. 5-star rated handyman in Abergavenny & Monmouthshire. Quality carpentry & repairs.",
    canonical: `${SITE_ORIGIN}/reviews`,
    ogType: "website",
  });

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await axios.get(`${API}/feedback/approved`);
        setReviews(response.data);
      } catch (error) {
        console.error("Error fetching reviews:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchReviews();

    // Track page view
    const trackPageView = async () => {
      try {
        let sessionId = sessionStorage.getItem('shs_session_id');
        if (!sessionId) {
          sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          sessionStorage.setItem('shs_session_id', sessionId);
        }
        await axios.post(`${API}/analytics/pageview`, null, {
          params: { page: '/reviews', session_id: sessionId }
        });
      } catch (error) { console.error('Failed to fetch reviews:', error); }
    };
    trackPageView();
  }, []);

  // Calculate average rating
  const avgRating = reviews.length > 0 
    ? (reviews.reduce((sum, r) => sum + r.overall_rating, 0) / reviews.length).toFixed(1)
    : 0;

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <PublicNav />

      {/* Main Content */}
      <div className="pt-24 pb-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Back Link */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-6"
          >
            <ChevronLeft className="h-4 w-4" />
            <span>Back to Home</span>
          </Link>

          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-3xl md:text-4xl font-bold font-heading mb-4">Customer Reviews</h1>
            {reviews.length > 0 && (
              <div className="flex items-center justify-center gap-3">
                <div className="flex items-center gap-1">
                  <Star className="h-6 w-6 fill-yellow-400 text-yellow-400" />
                  <span className="text-2xl font-bold">{avgRating}</span>
                </div>
                <span className="text-muted-foreground">from {reviews.length} review{reviews.length !== 1 ? 's' : ''}</span>
              </div>
            )}
          </div>

          {/* Reviews List */}
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            </div>
          ) : reviews.length > 0 ? (
            <div className="space-y-6">
              {reviews.map((review) => (
                <Card key={review.id} className="overflow-hidden">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="bg-primary/10 rounded-full p-3 shrink-0">
                        <Quote className="h-5 w-5 text-primary" />
                      </div>
                      <div className="flex-1">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                          <div>
                            <h3 className="font-semibold">{review.customer_name}</h3>
                            <p className="text-sm text-muted-foreground capitalize">
                              {review.service_type?.replace('-', ' ')}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <StarDisplay rating={review.overall_rating} />
                            <span className="text-sm text-muted-foreground">
                              {new Date(review.created_at).toLocaleDateString('en-GB', { 
                                month: 'short', 
                                year: 'numeric' 
                              })}
                            </span>
                          </div>
                        </div>
                        {review.comments && (
                          <p className="text-muted-foreground italic">&ldquo;{review.comments}&rdquo;</p>
                        )}
                        {review.recommend && (
                          <p className="text-sm text-green-600 mt-3 flex items-center gap-1">
                            <span className="text-green-500">✓</span> Would recommend
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">No reviews yet</p>
              <Link to="/feedback">
                <Button>Be the first to leave a review</Button>
              </Link>
            </div>
          )}

          {/* CTA */}
          {reviews.length > 0 && (
            <div className="text-center mt-12">
              <p className="text-muted-foreground mb-4">Had work done by us?</p>
              <Link to="/feedback">
                <Button size="lg">
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Leave Your Review
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>

      <PublicFooter bizInfo={bizInfo} services={services} />
    </div>
  );
}
